import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, MousePointer, TrendingUp, Clock, Calendar, LogOut, BarChart3, Users, UserCheck, Star, Globe, Settings } from 'lucide-react';
import CountryDetailsModal from './CountryDetailsModal';
import DetailedViewsModal from './DetailedViewsModal';
import RecentViewsModal from './RecentViewsModal';
import EarlyAccessRequestModal from './EarlyAccessRequestModal';
import PrivateAccessSettings from './PrivateAccessSettings';

const AdminDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showDetailedViewsModal, setShowDetailedViewsModal] = useState(false);
  const [showRecentViewsModal, setShowRecentViewsModal] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState(null);
  
  // Early Access states
  const [earlyAccessRequests, setEarlyAccessRequests] = useState([]);
  const [earlyAccessStats, setEarlyAccessStats] = useState(null);
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Private Access Settings state
  const [showPrivateAccessSettings, setShowPrivateAccessSettings] = useState(false);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          onLogout();
          return;
        }
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEarlyAccessData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch requests
      const requestsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/early-access-requests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (requestsResponse.ok) {
        const requestsData = await requestsResponse.json();
        setEarlyAccessRequests(requestsData);
      }

      // Fetch stats
      const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/early-access-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setEarlyAccessStats(statsData);
      }
    } catch (err) {
      console.error('Error fetching early access data:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchEarlyAccessData();
    const interval = setInterval(() => {
      fetchStats();
      fetchEarlyAccessData();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('adminToken');
      onLogout();
    }
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
    setShowEarlyAccessModal(true);
  };

  const handleCloseEarlyAccessModal = () => {
    setShowEarlyAccessModal(false);
    setSelectedRequest(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-white">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-400"></div>
          <p className="mt-4 text-center">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <h2 className="text-2xl font-bold mb-4">Erreur</h2>
          <p>{error}</p>
          <button 
            onClick={fetchStats}
            className="mt-4 px-6 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <motion.header 
        className="bg-white/10 backdrop-blur-md border-b border-white/10"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">123zzz Analytics</h1>
                <p className="text-blue-200 text-sm">Dashboard Administrateur</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowPrivateAccessSettings(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Private Access</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <StatCard
            title="Vues totales"
            value={stats.total_page_views}
            icon={Eye}
            color="from-blue-500 to-blue-600"
            subtitle="Toutes les pages"
            onClick={() => setShowCountryModal(true)}
            clickable={true}
          />
          <StatCard
            title="Clics Get Started"
            value={stats.total_get_started_clicks}
            icon={MousePointer}
            color="from-green-500 to-green-600"
            subtitle="Total historique"
          />
          <StatCard
            title="Vues aujourd'hui"
            value={stats.page_views_today}
            icon={Calendar}
            color="from-purple-500 to-purple-600"
            subtitle="Dernières 24h"
            onClick={() => setShowRecentViewsModal(true)}
            clickable={true}
          />
          <StatCard
            title="Clics aujourd'hui"
            value={stats.get_started_clicks_today}
            icon={TrendingUp}
            color="from-orange-500 to-orange-600"
            subtitle="Get Started"
          />
        </motion.div>

        {/* Charts */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
          {/* Weekly Trend */}
          <motion.div 
            className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 p-6"
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2" />
              Tendance 7 derniers jours
            </h3>
            <WeeklyChart 
              pageViews={stats.page_views_last_7_days}
              clicks={stats.get_started_clicks_last_7_days}
            />
          </motion.div>

          {/* Hourly Activity */}
          <motion.div 
            className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 p-6"
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Activité par heure (aujourd'hui)
            </h3>
            <HourlyChart data={stats.hourly_stats_today} />
          </motion.div>
        </div>

        {/* Conversion Rate */}
        <motion.div 
          className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 p-6"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h3 className="text-xl font-bold text-white mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Taux de conversion
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">
                {stats.total_page_views > 0 
                  ? ((stats.total_get_started_clicks / stats.total_page_views) * 100).toFixed(1)
                  : 0}%
              </div>
              <div className="text-gray-300 text-sm">Conversion globale</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400">
                {stats.page_views_today > 0 
                  ? ((stats.get_started_clicks_today / stats.page_views_today) * 100).toFixed(1)
                  : 0}%
              </div>
              <div className="text-gray-300 text-sm">Conversion aujourd'hui</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-400">
                {stats.total_page_views > 0 
                  ? (stats.total_get_started_clicks / stats.total_page_views * 1000).toFixed(1)
                  : 0}‰
              </div>
              <div className="text-gray-300 text-sm">Clics par 1000 vues</div>
            </div>
          </div>
        </motion.div>

        {/* Early Access Requests Section */}
        <motion.div 
          className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 p-6 mt-8"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center">
              <UserCheck className="w-5 h-5 mr-2" />
              Demandes d'Accès Anticipé
            </h3>
            {earlyAccessStats && (
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">{earlyAccessStats.total_requests}</div>
                  <div className="text-xs text-gray-300">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">{earlyAccessStats.recent_requests}</div>
                  <div className="text-xs text-gray-300">7 derniers jours</div>
                </div>
              </div>
            )}
          </div>

          {/* Stats Cards for Early Access */}
          {earlyAccessStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center">
                  <Globe className="w-4 h-4 mr-2" />
                  Top Pays
                </h4>
                <div className="space-y-2">
                  {earlyAccessStats.top_countries.slice(0, 5).map((country, index) => (
                    <div key={country._id} className="flex justify-between items-center">
                      <span className="text-gray-300 text-sm">{country._id}</span>
                      <span className="text-white font-medium">{country.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-green-500/20 to-teal-500/20 rounded-xl p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Taille des Familles
                </h4>
                <div className="space-y-2">
                  {earlyAccessStats.family_sizes.slice(0, 5).map((size, index) => (
                    <div key={size._id} className="flex justify-between items-center">
                      <span className="text-gray-300 text-sm">Family of {size._id}</span>
                      <span className="text-white font-medium">{size.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Recent Requests Table */}
          <div className="bg-black/20 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/10">
              <h4 className="text-white font-semibold">Demandes Récentes</h4>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {earlyAccessRequests.length > 0 ? (
                <div className="divide-y divide-white/10">
                  {earlyAccessRequests.slice(0, 10).map((request) => (
                    <motion.div
                      key={request.id}
                      className="px-6 py-4 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => handleRequestClick(request)}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {request.first_name[0]}{request.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <div className="text-white font-medium">
                              {request.first_name} {request.last_name}
                            </div>
                            <div className="text-gray-400 text-sm flex items-center space-x-2">
                              <Globe className="w-3 h-3" />
                              <span>{request.country}</span>
                              <span>•</span>
                              <Users className="w-3 h-3" />
                              <span>Family of {request.number_of_children}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-300 text-sm">
                            {new Date(request.timestamp).toLocaleDateString('fr-FR')}
                          </div>
                          <div className="text-gray-400 text-xs">
                            {new Date(request.timestamp).toLocaleTimeString('fr-FR', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 text-gray-300 text-sm truncate">
                        "{request.message.substring(0, 80)}..."
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-8 text-center">
                  <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <div className="text-gray-400">Aucune demande d'accès anticipé pour le moment</div>
                  <div className="text-gray-500 text-sm mt-1">Les nouvelles demandes apparaîtront ici</div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Country Details Modal */}
      <CountryDetailsModal
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        stats={stats}
        onCountryClick={(countryCode) => {
          setSelectedCountryCode(countryCode);
          setShowCountryModal(false);
          setShowDetailedViewsModal(true);
        }}
      />

      {/* Detailed Views Modal */}
      <DetailedViewsModal
        isOpen={showDetailedViewsModal}
        onClose={() => {
          setShowDetailedViewsModal(false);
          setSelectedCountryCode(null);
        }}
        countryCode={selectedCountryCode}
      />

      {/* Recent Views Modal */}
      <RecentViewsModal
        isOpen={showRecentViewsModal}
        onClose={() => setShowRecentViewsModal(false)}
      />

      {/* Early Access Request Modal */}
      <EarlyAccessRequestModal
        request={selectedRequest}
        isOpen={showEarlyAccessModal}
        onClose={handleCloseEarlyAccessModal}
      />

      {/* Private Access Settings Modal */}
      {showPrivateAccessSettings && (
        <PrivateAccessSettings
          onClose={() => setShowPrivateAccessSettings(false)}
        />
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick, clickable = false }) => (
  <motion.div 
    className={`bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 p-6 ${clickable ? 'cursor-pointer hover:bg-white/15 hover:border-white/20' : ''}`}
    whileHover={{ scale: clickable ? 1.02 : 1.005, y: -2 }}
    transition={{ type: "spring", stiffness: 300 }}
    onClick={onClick}
  >
    <div className="flex items-center justify-between mb-4">
      <div className={`w-12 h-12 bg-gradient-to-r ${color} rounded-xl flex items-center justify-center relative`}>
        <Icon className="w-6 h-6 text-white" />
        {clickable && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-white/30 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          </div>
        )}
      </div>
    </div>
    <div className="text-3xl font-bold text-white mb-1">{value.toLocaleString()}</div>
    <div className="text-gray-300 text-sm">{title}</div>
    <div className="text-gray-400 text-xs mt-1">
      {subtitle}
      {clickable && <span className="ml-2 text-blue-400">• Cliquez pour détails</span>}
    </div>
  </motion.div>
);

const WeeklyChart = ({ pageViews, clicks }) => {
  const maxValue = Math.max(
    ...pageViews.map(d => d.count),
    ...clicks.map(d => d.count)
  );

  // Si toutes les données sont à 0, on affiche un message
  const hasData = maxValue > 0;

  return (
    <div className="h-64">
      {!hasData ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center text-gray-400">
            <div className="text-lg mb-2">📊</div>
            <div className="text-sm">Pas encore de données</div>
            <div className="text-xs mt-1">Les graphiques apparaîtront dès les premières visites</div>
          </div>
        </div>
      ) : (
        <div className="flex items-end justify-between h-full space-x-2">
          {pageViews.map((day, index) => {
            const clicksForDay = clicks[index]?.count || 0;
            // Hauteur minimum de 2% pour que les barres soient visibles
            const viewHeight = Math.max((day.count / maxValue) * 100, day.count > 0 ? 2 : 0);
            const clickHeight = Math.max((clicksForDay / maxValue) * 100, clicksForDay > 0 ? 2 : 0);
            
            return (
              <div key={day.date} className="flex-1 flex flex-col items-center">
                <div className="flex items-end space-x-1 flex-1 w-full">
                  <motion.div
                    className="bg-blue-400 rounded-t-sm flex-1 relative group"
                    style={{ height: `${viewHeight}%` }}
                    initial={{ height: 0 }}
                    animate={{ height: `${viewHeight}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-black/80 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {day.count} vues
                    </div>
                  </motion.div>
                  <motion.div
                    className="bg-green-400 rounded-t-sm flex-1 relative group"
                    style={{ height: `${clickHeight}%` }}
                    initial={{ height: 0 }}
                    animate={{ height: `${clickHeight}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-black/80 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {clicksForDay} clics
                    </div>
                  </motion.div>
                </div>
                <div className="text-xs text-gray-300 mt-2">
                  {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex justify-center mt-4 space-x-6">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-400 rounded mr-2"></div>
          <span className="text-gray-300 text-sm">Vues de page</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-400 rounded mr-2"></div>
          <span className="text-gray-300 text-sm">Clics Get Started</span>
        </div>
      </div>
    </div>
  );
};

const HourlyChart = ({ data }) => {
  const maxValue = Math.max(...data.map(d => Math.max(d.page_views, d.clicks)));

  // Si toutes les données sont à 0, on affiche un message
  const hasData = maxValue > 0;

  return (
    <div className="h-64">
      {!hasData ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center text-gray-400">
            <div className="text-lg mb-2">⏰</div>
            <div className="text-sm">Aucune activité aujourd'hui</div>
            <div className="text-xs mt-1">Les données apparaîtront dès les premières visites</div>
          </div>
        </div>
      ) : (
        <div className="flex items-end justify-between h-full">
          {data.map((hour) => {
            // Hauteur minimum de 2% pour que les barres soient visibles
            const viewHeight = Math.max((hour.page_views / maxValue) * 100, hour.page_views > 0 ? 2 : 0);
            const clickHeight = Math.max((hour.clicks / maxValue) * 100, hour.clicks > 0 ? 2 : 0);
            
            return (
              <div key={hour.hour} className="flex flex-col items-center flex-1">
                <div className="flex items-end space-x-1 flex-1 w-full">
                  <motion.div
                    className="bg-purple-400 rounded-t-sm flex-1 relative group"
                    style={{ height: `${viewHeight}%` }}
                    initial={{ height: 0 }}
                    animate={{ height: `${viewHeight}%` }}
                    transition={{ duration: 0.3, delay: hour.hour * 0.02 }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-black/80 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {hour.page_views} vues
                    </div>
                  </motion.div>
                  <motion.div
                    className="bg-orange-400 rounded-t-sm flex-1 relative group"
                    style={{ height: `${clickHeight}%` }}
                    initial={{ height: 0 }}
                    animate={{ height: `${clickHeight}%` }}
                    transition={{ duration: 0.3, delay: hour.hour * 0.02 + 0.1 }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-black/80 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {hour.clicks} clics
                    </div>
                  </motion.div>
                </div>
                {hour.hour % 4 === 0 && (
                  <div className="text-xs text-gray-300 mt-1">
                    {hour.hour}h
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="flex justify-center mt-4 space-x-6">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-purple-400 rounded mr-2"></div>
          <span className="text-gray-300 text-sm">Vues</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-orange-400 rounded mr-2"></div>
          <span className="text-gray-300 text-sm">Clics</span>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;